import { useContext, useEffect, useState } from "react";
import { DisaggregatedMetric, GeoDetails, GeoTag, LayerData } from "../services/types";

import printWhiteSvg from '../assets/print-white.svg';
import MetricCategoriesContext from "../context/MetricCategoriesContext";
import LoadingIndicator from "../components/LoadingIndicator";
import Header from "../components/pdf/Header";
import { useOrgContext } from "../context/OrgIdContext";
import { formatValue } from "../utils/helpers";
import SnapshotDashboard from "../components/snapshot/SnapshotDashboard";
import { indicatorsEnv } from "../utils/environment";


interface ProgrammaticModalProps {
    isOpen: boolean;
    onClose: (e: React.MouseEvent<HTMLButtonElement>) => void;
    layers: LayerData[];
    geoDetails: GeoDetails[];
    geoTag: GeoTag | null;
    pdfUrl: string;
    logoUrl: string;
    selectedArea: string;
    siteUrl: string;
}

const ProgrammaticModal: React.FC<ProgrammaticModalProps> = ({ isOpen, onClose, layers, geoDetails, geoTag, selectedArea }) => {
    const { metricCategories } = useContext(MetricCategoriesContext);
    const { orgId } = useOrgContext();
    const [loading, setLoading] = useState(true);
    const [disaggregatedData, setDisaggregatedData] = useState<DisaggregatedMetric[]>([]);
    const geoEncoding = 'delta';
    const indicatorsUrl = `${import.meta.env.VITE_DASHBOARD_BACKEND_URL}/api/getallindicatorsv2`;
    const validLayers = layers?.filter(layer => layer.items && layer.items.length > 0);

    const fetchData = async () => {
        if (!isOpen || !orgId || metricCategories.length === 0) {
            setLoading(false);
            return;
        } 
    
        try {
            setLoading(true);
    
            if (orgId !== null) {
                // Generate an array of fetch promises, one for each category
                const fetchPromises = metricCategories.map(category => 
                    fetch(indicatorsUrl, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify({
                            orgId,
                            geoDetails,
                            dashboardView: category,
                            geoEncoding,
                            geoTag,
                            allowDefault: true,
                            environment: indicatorsEnv
                        }),
                    }).then(response => {
                        if (!response.ok) {
                            throw new Error(`Failed to fetch data for ${category}. Response: ${response}`);
                        }
                        return response.json();
                    })
                );
    
                // Resolve all promises in parallel
                const results = await Promise.all(fetchPromises);
                console.log('results from category calls:', results);
    
                // Combine and categorize the results
                const combinedData = results.flat();
                setDisaggregatedData(combinedData);
            }
        } catch (error) {
            console.error('Error fetching data:', error);
            // setError(error);
        } finally {
            setLoading(false);
        }
    };

    // Hide main scrollbar when the metadata panel is open
    useEffect(() => {
        if (isOpen) {
          document.body.style.overflow = 'hidden';
        } else {
          document.body.style.overflow = '';
        }

        return () => {
          document.body.style.overflow = '';
        };
    }, [isOpen]);

    useEffect(() => {
        if (isOpen) {
            console.log('Effect triggered:', { isOpen, orgId, metricCategories });
            fetchData();
        }
    }, [isOpen, orgId, metricCategories]);

    const handlePrint = () => {
        const originalTitle = document.title;
        document.title = "ProgrammaticPDF";
        window.print();
        document.title = originalTitle;  // Restore the original title
    };

    if (loading) {
        return <LoadingIndicator/>
    }

    return (
        <div id="print-container" className="fixed inset-0 bg-white p-4 overflow-y-auto z-50">
            {/* Wrapper to limit the modal's width */}
            <div className="max-w-[800px] mx-auto w-full mb-8">
                <div className="relative h-full w-full">
                    <button
                        type="button"
                        aria-label="Close modal"
                        onClick={onClose}
                        className="absolute top-4 right-4 text-gray-500 hover:text-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                    {/* Print and Close buttons */}
                    <div className="flex justify-center no-print mb-[1rem]">
                        <button 
                            onClick={handlePrint}
                            className="flex mx-2 bg-color-cai-blue text-white py-2 px-4 rounded-md hover:bg-blue-500"
                        >
                            <img src={printWhiteSvg} alt="Print" className="w-5 h-5 mr-2" />
                            <p className="text-sm">Print</p>
                        </button>
                        <button 
                            onClick={onClose}
                            className="bg-transparent border border-color-cai-lightGray text-color-cai-dashboard py-2 px-4 rounded-md hover:bg-color-cai-lightGray"
                        >
                            <p className="text-sm">Close</p>
                        </button>
                    </div>

                    {/* Regional Metric Data */}
                    <div className="page">
                        <div className="w-full h-auto p-4 flex justify-between items-center bg-color-cai-blue my-4">
                            <div className="flex items-center justify-start mx-1 w-full">
                                <div className="flex flex-col items-start justify-start">
                                <p className="text-[#E8E8E8] text-[32px] font-normal mr-4 w-80">Regional Profile</p>
                                {selectedArea && <p className="text-sm font-normal text-white text-opacity-80">{selectedArea}</p>}
                                </div>
                                <p className="pl-8 text-sm font-normal text-white text-opacity-80 border-l border-white border-opacity-20">This section represents a summary of the contextual data for the area selected. Below is a regional profile of metrics related to: <br></br>
                                {metricCategories.map((category, index) => {
                                const isLast = index === metricCategories.length - 1;
                                const isSecondLast = index === metricCategories.length - 2;
                                return (
                                    <span key={category}>
                                    {category}
                                    {isLast ? '.' : isSecondLast ? ', and ' : ', '}
                                    </span>
                                );
                                })}</p>
                            </div>
                        </div>
                        <div className="content">
                            <SnapshotDashboard data={disaggregatedData}/>
                        </div>
                    </div>

                    {/* Programmatic Data */}
                    <div>
                    {validLayers.map((layer) => {
                        const layerUnit = layer.layer_units?.find(unit => unit.type === 'item');

                        return (
                            <div key={layer.layer_id} className="page">
                                <Header selectedArea={selectedArea} bgColor={'bg-port-blue'} text={layer.layer_name} />
                                <div className="content px-2 pt-0 pb-8 text-sm text-gray-800">
                                    <p className="italic text-gray-600 text-xs text-end">
                                        Data last
                                        updated on <strong>{new Date(layer.items_updated_at || Date.now()).toLocaleDateString()}</strong>.
                                    </p>
                                    <p className="mt-4 mb-8 italic text-gray-600">{layer.layer_description || 'No description provided.'}</p>

                                    {/* Summary Section */}
                                    {layer.summary && layer.summary.length > 0 && (
                                        <div className="mb-6">
                                            <h3 className="font-semibold text-md mb-1">{layer.layer_name} Summary</h3>
                                            <table className="table-auto border-collapse border border-gray-300 text-sm shadow-md">
                                                <thead>
                                                    <tr>
                                                        <th className="border border-gray-300 px-4 py-2 text-white bg-port-blue min-w-36 text-start">Items</th>
                                                        <th className="border border-gray-300 px-4 py-2 text-white bg-port-blue min-w-36 text-start">Total</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    <tr>
                                                        <td className="border border-gray-300 px-4 py-2">{formatValue(layer.summary[0].item_count, 'integer')}</td>
                                                        <td className="border border-gray-300 px-4 py-2">{formatValue(layer.summary[0].item_total, 'integer')}</td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </div>
                                    )}

                                    {/* Attribute Tallies */}
                                    {layer.summary?.[0]?.attribute_tallies?.map((attribute: any) => { 
                                        const total = attribute.tallies.reduce((sum: any, t: any) => sum + (t.value || 0), 0);

                                        return (
                                            <div key={attribute.name} className="break-card mb-6">
                                                <h4 className="font-semibold text-md mb-1">Attribute Table - {attribute.name}</h4>
                                                <table className="table-auto border-collapse border border-gray-300 text-sm w-full shadow-md">
                                                    <thead>
                                                        <tr>
                                                            <th className="border border-gray-300 bg-port-green px-2 py-1 text-left text-white">{attribute.name}</th>
                                                            <th className="border border-gray-300 bg-port-green px-2 py-1 text-left text-white">{`Count ${layerUnit ? '(' + layerUnit.unit_descriptor + ')' : ''}`}</th>
                                                            <th className="border border-gray-300 bg-port-green px-2 py-1 text-left text-white">Percentage</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {attribute.tallies.map((tally: any, i: any) => (
                                                            <tr key={i}>
                                                                <td className="border border-gray-300 px-2 py-1">{tally.name}</td>
                                                                <td className="border border-gray-300 px-2 py-1">{formatValue(tally.value, 'integer')}</td>
                                                                <td className="border border-gray-300 px-2 py-1">
                                                                    {total > 0 ? `${((tally.value / total) * 100).toFixed(1)}%` : '—'}
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        )
                                    })}
                                </div>
                            </div>
                        )}
                    )}    
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ProgrammaticModal;